/**
 * Identity Provider Detail Page
 *
 * Displays full provider configuration with tabs for overview, configuration,
 * and tenant bindings. Supports edit, suspend, activate, and delete operations.
 *
 * Read flow: useIdentityProvider (React Query useQuery)
 * Write flows: useUpdateIdentityProvider, useSuspendIdentityProvider,
 *              useActivateIdentityProvider, useDeleteIdentityProvider
 *              (React Query useMutation, all wired to real PUT/POST/DELETE)
 *
 * The "Edit Configuration" button delegates to CreateIdpModal in edit mode,
 * which composes the update payload and submits via the mutation hook.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft as BackIcon } from 'lucide-react';
import { Alert, Card, Button, Tabs, Textarea, Dialog, DataTable, Input, Badge, Select, type TabItem, type DataTableColumn } from '@aaimplatform/ui';
import { CreateIdpModal } from '../components/CreateIdpModal';
import {
  useIdentityProvider,
  useSuspendIdentityProvider,
  useActivateIdentityProvider,
  useDeleteIdentityProvider,
} from '../hooks/useIdentityProviders';
import {
  useTenantIdentityBindings,
  useBindTenantIdp,
  useRemoveTenantIdentityBinding,
} from '../hooks/useTenantIdentityBindings';
import type { components } from '@aaimplatform/institutional-types/identity-provider-registry';

type TenantIdentityBinding = components['schemas']['TenantIdentityBinding'];

const BINDING_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  suspended: 'warning',
  pending: 'neutral',
};

const TABS: TabItem[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'tenant-bindings', label: 'Tenant Bindings' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-growth/10 text-growth',
  suspended: 'bg-warning/10 text-warning',
  pending_configuration: 'bg-recessed text-secondary',
  decommissioned: 'bg-error/10 text-error',
};

export function IdentityProviderDetailPage() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showBindDialog, setShowBindDialog] = useState(false);
  const [bindTenantId, setBindTenantId] = useState('');
  const [bindIsPrimary, setBindIsPrimary] = useState('false');
  const [bindError, setBindError] = useState<string | null>(null);

  const providerQuery = useIdentityProvider(providerId);
  const suspendMutation = useSuspendIdentityProvider();
  const activateMutation = useActivateIdentityProvider();
  const deleteMutation = useDeleteIdentityProvider();
  // rbac-03b (#1286): tenant bindings for THIS provider (cross-tenant view).
  const bindingsQuery = useTenantIdentityBindings(providerId);
  const bindMutation = useBindTenantIdp();
  const removeBindingMutation = useRemoveTenantIdentityBinding(providerId);

  const provider = providerQuery.data;
  const loading = providerQuery.isLoading;
  const queryError = providerQuery.error;
  const isNotFound =
    !!queryError && /404|not found/i.test(queryError instanceof Error ? queryError.message : String(queryError));
  const actionLoading =
    suspendMutation.isPending || activateMutation.isPending || deleteMutation.isPending;

  async function handleSuspend() {
    if (!provider || !suspendReason.trim()) return;
    setActionError(null);
    try {
      // Suspend reason is logged server-side via the audit trail.
      await suspendMutation.mutateAsync(provider.id);
      setShowSuspendDialog(false);
      setSuspendReason('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to suspend provider');
    }
  }

  async function handleActivate() {
    if (!provider) return;
    setActionError(null);
    try {
      await activateMutation.mutateAsync(provider.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to activate provider');
    }
  }

  async function handleDelete() {
    if (!provider) return;
    setActionError(null);
    try {
      await deleteMutation.mutateAsync(provider.id);
      navigate('/identity-providers');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete provider');
    }
  }

  const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  async function handleBindTenant() {
    if (!provider) return;
    const tenantId = bindTenantId.trim();
    if (!UUID_RE.test(tenantId)) {
      setBindError('Tenant ID must be a valid UUID');
      return;
    }
    setBindError(null);
    try {
      await bindMutation.mutateAsync({
        tenantId,
        identityProviderId: provider.id,
        isPrimary: bindIsPrimary === 'true',
      });
      setShowBindDialog(false);
      setBindTenantId('');
      setBindIsPrimary('false');
    } catch (err) {
      setBindError(err instanceof Error ? err.message : 'Failed to bind tenant');
    }
  }

  async function handleRemoveBinding(bindingId: string) {
    setActionError(null);
    try {
      await removeBindingMutation.mutateAsync(bindingId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove binding');
    }
  }

  const bindingColumns: DataTableColumn<TenantIdentityBinding>[] = [
    { header: 'Tenant ID', accessor: 'tenantId', monospace: true },
    {
      header: 'Primary',
      accessor: 'isPrimary',
      cell: (value) =>
        value ? (
          <Badge variant="info">Primary</Badge>
        ) : (
          <span className="text-tertiary">--</span>
        ),
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (value) => (
        <Badge variant={BINDING_STATUS_VARIANT[String(value)] ?? 'neutral'}>
          {String(value)}
        </Badge>
      ),
    },
    {
      header: '',
      accessor: 'id',
      align: 'right',
      cell: (_value, row) => (
        <Button
          variant="ghost"
          onClick={() => handleRemoveBinding(row.id)}
          disabled={removeBindingMutation.isPending || row.isPrimary}
          className="text-error hover:bg-error/10"
        >
          {row.isPrimary ? 'Primary' : 'Remove'}
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-secondary">Loading provider details...</div>
      </div>
    );
  }

  if (isNotFound) {
    return (
      <div className="p-6">
        <Alert type="warning" title="Identity provider not found">
          <div>The provider you requested does not exist or has been decommissioned.</div>
          <Button variant="ghost" onClick={() => navigate('/identity-providers')} className="mt-4">
            Back to Providers
          </Button>
        </Alert>
      </div>
    );
  }

  if (queryError || !provider) {
    return (
      <div className="p-6">
        <Alert type="error" title="Error">
          <div>{queryError instanceof Error ? queryError.message : 'Provider not found'}</div>
          <Button variant="ghost" onClick={() => navigate('/identity-providers')} className="mt-4">
            Back to Providers
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={() => navigate('/identity-providers')}
        className="flex items-center gap-2 text-sm text-secondary hover:text-primary mb-4 transition-colors"
      >
        <BackIcon size={16} />
        <span>Back to Identity Providers</span>
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3 text-sm text-secondary">
              <span>Type: {provider.providerType.toUpperCase()}</span>
              <span className="text-tertiary">|</span>
              <span>Family: {provider.providerFamily}</span>
              <span className="text-tertiary">|</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[provider.status]}`}>
                {provider.status.replace('_', ' ')}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {provider.status === 'active' && (
              <Button variant="secondary" onClick={() => setShowSuspendDialog(true)} disabled={actionLoading}>
                Suspend
              </Button>
            )}
            {provider.status === 'suspended' && (
              <Button variant="primary" onClick={handleActivate} disabled={actionLoading}>
                Activate
              </Button>
            )}
            <Button variant="secondary" onClick={() => setEditModalOpen(true)}>
              Edit Configuration
            </Button>
          </div>
        </div>
      </div>

      {/* Action Error Banner */}
      {actionError && (
        <Alert type="error" title="Action failed" className="mb-4">
          {actionError}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-base font-sans font-semibold text-primary mb-4">Provider Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-sans text-tertiary mb-1">Provider ID</div>
                  <div className="text-sm font-mono text-secondary">{provider.id}</div>
                </div>
                <div>
                  <div className="text-xs font-sans text-tertiary mb-1">Status</div>
                  <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[provider.status]}`}>
                    {provider.status.replace('_', ' ')}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-sans text-tertiary mb-1">Created At</div>
                  <div className="text-sm text-secondary">{provider.createdAt ? new Date(provider.createdAt).toLocaleString() : 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs font-sans text-tertiary mb-1">Last Updated</div>
                  <div className="text-sm text-secondary">{provider.updatedAt ? new Date(provider.updatedAt).toLocaleString() : 'N/A'}</div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-base font-sans font-semibold text-primary mb-4">Security Settings</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondary">Auto-provision users</span>
                  <span className="text-sm font-medium text-primary">
                    {provider.autoProvisionUsers ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondary">Require MFA</span>
                  <span className="text-sm font-medium text-primary">
                    {provider.requireMfa ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondary">Max session duration</span>
                  <span className="text-sm font-medium text-primary">
                    {provider.maxSessionDuration} seconds ({Math.floor(provider.maxSessionDuration / 60)} minutes)
                  </span>
                </div>
              </div>
            </Card>

            {/* Delete moved into the Edit Configuration modal footer (2026-07-24
                operator UX correction): a page-flow "Danger Zone" Alert card read
                as an error banner sitting in the layout. The destructive action
                now lives beside the other provider-config controls, behind the
                same confirmation Dialog. */}
          </div>
        )}

        {activeTab === 'configuration' && (
          <Card className="p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-base font-sans font-semibold text-primary">Protocol Configuration</h3>
              <Button variant="ghost" onClick={() => setEditModalOpen(true)}>
                Edit
              </Button>
            </div>
            <div className="space-y-4">
              {['oidc', 'oauth2'].includes(provider.providerType) && (
                <>
                  <div>
                    <div className="text-xs font-sans text-tertiary mb-1">Client ID</div>
                    <div className="text-sm font-mono text-secondary">{provider.clientId || 'Not configured'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-sans text-tertiary mb-1">Discovery URL</div>
                    <div className="text-sm font-mono text-secondary break-all">{provider.discoveryUrl || 'Not configured'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-sans text-tertiary mb-1">Redirect URI</div>
                    <div className="text-sm font-mono text-secondary break-all">{provider.redirectUri || 'Not configured'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-sans text-tertiary mb-1">Scopes</div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {provider.scopes?.map((scope) => (
                        <span key={scope} className="px-2 py-1 bg-surface text-xs font-mono rounded">
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {provider.providerType === 'saml' && (
                <>
                  <div>
                    <div className="text-xs font-sans text-tertiary mb-1">Metadata URL</div>
                    <div className="text-sm font-mono text-secondary break-all">{provider.samlMetadataUrl || 'Not configured'}</div>
                  </div>
                  <div>
                    <div className="text-xs font-sans text-tertiary mb-1">Entity ID</div>
                    <div className="text-sm font-mono text-secondary break-all">{provider.samlEntityId || 'Not configured'}</div>
                  </div>
                  {provider.samlCertificate && (
                    <div>
                      <div className="text-xs font-sans text-tertiary mb-1">Certificate</div>
                      <pre className="text-xs font-mono text-secondary bg-surface p-3 rounded overflow-x-auto">
                        {provider.samlCertificate}
                      </pre>
                    </div>
                  )}
                </>
              )}

              {provider.providerType === 'mx_auth' && (
                <>
                  <div>
                    <div className="text-xs font-sans text-tertiary mb-1">Quiltt Institution ID</div>
                    <div className="text-sm font-mono text-secondary break-all">
                      {provider.quilttInstitutionId || 'Not configured'}
                    </div>
                  </div>
                  {/* Contextual help: the MX-Auth custom IdP method (ADR-013).
                      Aaim-operator surface; gated at the portal boundary. */}
                  <div className="rounded-sm border border-recessed bg-surface px-4 py-3 space-y-2">
                    <p className="text-xs font-sans uppercase tracking-[0.15em] text-tertiary">
                      How MX Auth uses this provider
                    </p>
                    <p className="text-xs text-secondary">
                      MX Auth authenticates FI staff via a Quiltt Connect login to the FI&apos;s online
                      banking (ADR-013). The institution ID above is the FI&apos;s anti-spoof anchor:
                      staff sign-in reads the connection&apos;s institution server-side and rejects any
                      mismatch, and the identity service resolves this provider BY the value. It comes
                      from the one-time fishing procedure
                      (docs/operations/mx-auth-institution-id-fishing-runbook.md) -- never from the FI.
                    </p>
                    <p className="text-xs text-secondary">
                      Bind exactly one FI tenant under the Tenant Bindings tab (zero or multiple
                      active bindings fail staff sign-in closed), and keep this the only active
                      MX Auth provider carrying this institution ID. First-time staffers must also
                      present an emailed invitation token -- the invitation list is the staff
                      allowlist. Onboarding recipe: docs/operations/mx-auth-fi-onboarding-runbook.md.
                    </p>
                  </div>
                </>
              )}
            </div>
          </Card>
        )}

        {activeTab === 'tenant-bindings' && (
          <Card className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-sans font-semibold text-primary">Tenant Bindings</h3>
                <p className="text-sm text-secondary mt-1">
                  Tenants bound to this identity provider. Bind a tenant to route its
                  operators through this IdP{provider.isBuiltIn ? ' (the built-in Aaim Platform IdP)' : ''}.
                </p>
              </div>
              <Button variant="primary" onClick={() => { setBindError(null); setShowBindDialog(true); }}>
                Bind Tenant
              </Button>
            </div>

            {bindingsQuery.isLoading && (
              <div className="text-sm text-secondary py-4">Loading bindings...</div>
            )}

            {bindingsQuery.error && (
              <div className="text-sm text-error py-4">
                {bindingsQuery.error instanceof Error
                  ? bindingsQuery.error.message
                  : 'Failed to load tenant bindings'}
              </div>
            )}

            {!bindingsQuery.isLoading && !bindingsQuery.error && (
              <DataTable<TenantIdentityBinding>
                columns={bindingColumns}
                data={bindingsQuery.data ?? []}
                rowKey="id"
                hoverable
                emptyMessage="No tenants are bound to this provider yet."
              />
            )}
          </Card>
        )}
      </div>

      {/* Edit Modal -- composes the UpdateIdentityProviderRequest payload and
          submits via the typed PUT mutation. On success the modal closes and
          we invalidate the React Query cache so the page re-fetches the
          authoritative server state. */}
      <CreateIdpModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSuccess={() => {
          setEditModalOpen(false);
          providerQuery.refetch();
        }}
        editingProvider={provider}
        deleting={deleteMutation.isPending}
        onDelete={() => {
          // Close the edit modal and raise the existing delete confirmation
          // Dialog -- the destructive action keeps its own confirm step.
          setEditModalOpen(false);
          setShowDeleteDialog(true);
        }}
      />

      {/* Suspend Confirmation */}
      <Dialog
        open={showSuspendDialog}
        onOpenChange={(open) => { if (!open) setShowSuspendDialog(false); }}
        title="Suspend Provider"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowSuspendDialog(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSuspend}
              disabled={!suspendReason.trim() || actionLoading}
            >
              {suspendMutation.isPending ? 'Suspending...' : 'Suspend Provider'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-secondary mb-4">
          Suspending this provider will prevent all users from authenticating through it.
          Provide a reason for this action:
        </p>
        <Textarea
          label="Reason"
          value={suspendReason}
          onChange={(value) => setSuspendReason(value)}
          rows={3}
          placeholder="e.g., Security incident, maintenance, configuration issue"
        />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => { if (!open) setShowDeleteDialog(false); }}
        title="Delete Provider"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-error hover:bg-error/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Provider'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-secondary">
          Are you sure you want to delete &quot;{provider.name}&quot;? This action cannot be undone.
          All tenant bindings will be removed and users will lose access.
        </p>
      </Dialog>

      {/* Bind Tenant -- rbac-03b (#1286): POST /api/v1/tenant-identity-bindings
          via useBindTenantIdp. Binds an FI tenant to this provider so its
          operators authenticate through it. */}
      <Dialog
        open={showBindDialog}
        onOpenChange={(open) => { if (!open) { setShowBindDialog(false); setBindError(null); } }}
        title={`Bind Tenant to ${provider.name}`}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowBindDialog(false)} disabled={bindMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleBindTenant}
              disabled={!bindTenantId.trim() || bindMutation.isPending}
            >
              {bindMutation.isPending ? 'Binding...' : 'Bind Tenant'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-secondary">
            Bind a tenant (FI) to this identity provider. The tenant&apos;s operators
            will authenticate through {provider.name}.
          </p>
          <Input
            label="Tenant ID (UUID)"
            value={bindTenantId}
            onChange={(event) => setBindTenantId(event.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
          <Select
            label="Binding role"
            value={bindIsPrimary}
            onChange={(event) => setBindIsPrimary(event.target.value)}
            options={[
              { value: 'false', label: 'Secondary binding' },
              { value: 'true', label: 'Primary IdP for this tenant' },
            ]}
          />
          {bindError && <div className="text-sm text-error">{bindError}</div>}
        </div>
      </Dialog>
    </div>
  );
}
